import PropertyListItem from "@/components/property/PropertyListItem";
import { fetchSimilarProperties } from "@/lib/api/similarProperties";
import { Listing } from "@/lib/types/listingTypes";
import { PropertyType } from "@/lib/types/propertyTypes";
import {
  convertListingToPropertyType,
  convertPropertyTypeToListing,
} from "@/lib/utils/propertyTypeUtils";
import { ArrowRight, Building } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface SimilarPropertiesProps {
  property: Listing;
}

const SimilarProperties = ({ property }: SimilarPropertiesProps) => {
  const [similarProperties, setSimilarProperties] = useState<Listing[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getCityName = () => {
    if (property.location && typeof property.location === "object") {
      if ("city" in property.location) return (property.location as any).city;
      if ("name" in property.location) return property.location.name;
    }
    if (property.address && typeof property.address === "string") {
      const parts = property.address.split(",");
      if (parts.length >= 2) return parts[1].trim();
      if (parts.length === 1) return parts[0].trim();
    }
    return "this area";
  };

  useEffect(() => {
    async function loadSimilarProperties() {
      if (property && property.id) {
        setIsLoading(true);
        try {
          const propertyAsType: PropertyType =
            convertListingToPropertyType(property);
          const similarAsType: PropertyType[] = await fetchSimilarProperties(
            propertyAsType,
            3
          );
          const similarAsListing: Listing[] = similarAsType.map((p) =>
            convertPropertyTypeToListing(p)
          );
          setSimilarProperties(similarAsListing);
        } catch (error) {
          console.error("Error fetching/converting similar properties:", error);
          setSimilarProperties([]);
        } finally {
          setIsLoading(false);
        }
      }
    }

    loadSimilarProperties();
  }, [property]);

  if (!property || !property.id) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-4 md:p-6 border border-propertylk-blue/10">
      <div className="flex items-center justify-between gap-3 mb-4 md:mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 md:p-3 rounded-full bg-propertylk-lightblue text-propertylk-blue flex-shrink-0">
            <Building className="w-5 h-5 md:w-6 md:h-6" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base md:text-lg font-bold text-propertylk-darkblue truncate">
              Similar Properties
            </h2>
            <p className="text-gray-500 text-xs md:text-sm truncate">
              Explore more properties in {getCityName()}
            </p>
          </div>
        </div>
        <Link
          to="/properties"
          className="ml-auto flex items-center text-propertylk-blue hover:text-propertylk-darkblue group transition-colors flex-shrink-0 whitespace-nowrap text-xs md:text-sm"
        >
          <span className="font-medium mr-1">View all</span>
          <ArrowRight className="w-3 h-3 md:w-4 md:h-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100 animate-pulse"
            >
              <div className="aspect-video bg-gray-200 w-full" />
              <div className="p-3 md:p-4 space-y-3">
                <div className="h-4 bg-gray-200 rounded w-1/4" />
                <div className="h-5 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
                <div className="h-4 bg-gray-200 rounded w-full" />
                <div className="h-4 bg-gray-200 rounded w-1/3 mt-1" />
              </div>
            </div>
          ))}
        </div>
      ) : similarProperties.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {similarProperties.map((similarProperty) => {
            return (
              <PropertyListItem
                key={similarProperty.id}
                property={similarProperty}
              />
            );
          })}
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg p-6 text-center border border-gray-200">
          <p className="text-gray-700 font-medium">
            No similar properties found in this area.
          </p>
          <p className="text-gray-500 mt-2 text-sm">
            Try searching for other properties in our listings.
          </p>
          <Link
            to="/properties"
            className="inline-flex items-center mt-4 px-4 py-2 bg-propertylk-blue text-white rounded-md hover:bg-propertylk-darkblue transition-colors text-sm"
          >
            Browse all properties
            <ArrowRight className="ml-2 w-4 h-4" />
          </Link>
        </div>
      )}
    </div>
  );
};

export default SimilarProperties;
